import { useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import {
  preserveRuleBody,
  buildRuleBody,
  hasCompleteRuleCriteria,
} from './ruleBodyBuilder';
import { useTranslation } from '@refinedev/core';
import type { Rule } from '@billing/validation/rules';
import {
  ScopeMode,
  RuleSeverity,
  RuleMode,
  PatientAgeOperator,
  WhenLogic,
} from '@billing/validation/rules';
import { getRuleBuilderOptions } from './ruleClaimFieldOptions';

import type { RuleCondition, RuleEditorSnapshot } from './constants';
import {
  createEmptyAssertion,
  createEmptyApplyUpdate,
  createEmptyWhenClause,
  parseValueList,
  normalizeExactTariffValues,
  toSlug,
  buildWhenClauseCondition,
  buildValidationFailureCondition,
} from './ruleUtils';
import type { SetStateAction } from 'react';
import {
  createRuleBuilderState,
  updateRuleCriteriaClause,
  addRuleCriteriaClause,
  removeRuleCriteriaClause,
  addRuleCriteriaGroup,
  removeRuleCriteriaGroup,
  setRuleCriteriaGroupLogic,
  getRuleOutcomeSnapshot,
  isRuleOutcomeValid,
} from './ruleBuilderState';
import type { RuleBuilderState } from './ruleBuilderState';
import type {
  WhenClause,
  AssertionRow,
  ApplyUpdateRow,
  RuleAction,
} from './ruleSchemas';

/** Shared hook API consumed by rule creation, editing, previews, and field components. */
export type RuleBuilder = ReturnType<typeof useRuleBuilder>;

/**
 * Owns the rule editor's inputs, localized options, row actions, and save readiness.
 * Builds executable rules from form values, preserving unchanged
 * sections of existing rules when saving through the form.
 *
 * Initial values are read on mount; subsequent edits are managed by this hook.
 */
export const useRuleBuilder = ({
  initialBody,
  initialName = '',
  timezone = 'UTC',
}: {
  /** Saved body to populate the editor and preserve when sections are unchanged. */
  initialBody?: Rule['body'];
  /** Saved rule name; blank names use a name derived from the first When clause. */
  initialName?: string;
  /** Site timezone used to compile calendar dates into UTC comparison boundaries. */
  timezone?: string;
} = {}) => {
  /** Resolves editor labels and generated names using the active Refine locale. */
  const { translate } = useTranslation();

  /** Initializes editable inputs and row counters once; field meanings live in RuleBuilderState. */
  const [state, setState] = useState(() =>
    createRuleBuilderState(initialBody, initialName)
  );
  const {
    ruleName,
    severity,
    ruleMode,
    whenLogic,
    whenGroups,
    assertions,
    isFormSupported,
    applyUpdates,
    applyBehavior,
    applyNotification,
    ruleMessage,
    flagLabel,
    flagMessage,
    flagSeverity,
    applyIntervalMinutes,
    applyPriority,
    transformPriority,
    transformAppendFlags,
    transformCollapseByCode,
    transformApplyPercentages,
    timeCodeAfterMidnight,
    timeCodeEvening,
    timeCodeWeekend,
  } = state;

  const outcomeSnapshot = getRuleOutcomeSnapshot(state);

  /** Creates a typed setter accepting a value or updater; unchanged values retain the current state object. */
  const createFieldSetter =
    <K extends keyof RuleBuilderState>(key: K) =>
    (value: SetStateAction<RuleBuilderState[K]>): void => {
      setState((current) => {
        const next = typeof value === 'function' ? value(current[key]) : value;
        if (Object.is(current[key], next)) return current;
        return { ...current, [key]: next };
      });
    };

  /** Connects the name input to state; the effective name is derived separately. */
  const setRuleName = createFieldSetter('ruleName');
  /** Updates the alert/block severity used by validation outcomes. */
  const setSeverity = createFieldSetter('severity');
  /** Switches between requiring all groups and allowing any group to match. */
  const setWhenLogic = createFieldSetter('whenLogic');
  /** Replaces or updates validation check rows used by legacy rule controls. */
  const setAssertions = createFieldSetter('assertions');
  /** Stores the alert/block message before fallback text is applied. */
  const setRuleMessage = createFieldSetter('ruleMessage');
  /** Stores the flag title before the rule-name fallback is applied. */
  const setFlagLabel = createFieldSetter('flagLabel');
  /** Stores the flag description before generated fallback text is applied. */
  const setFlagMessage = createFieldSetter('flagMessage');
  /** Updates the display severity emitted by flag-only rules. */
  const setFlagSeverity = createFieldSetter('flagSeverity');
  /** Updates the selected minutes per unit for time-based apply-data outcomes. */
  const setApplyIntervalMinutes = createFieldSetter('applyIntervalMinutes');
  /** Stores apply-data priority input as text until event construction parses it. */
  const setApplyPriority = createFieldSetter('applyPriority');
  const setApplyBehavior = createFieldSetter('applyBehavior');
  const setApplyNotification = createFieldSetter('applyNotification');
  /** Stores tariff-transform priority input as text until event construction parses it. */
  const setTransformPriority = createFieldSetter('transformPriority');
  /** Controls whether the transform payload enables time flags in provider notes. */
  const setTransformAppendFlags = createFieldSetter('transformAppendFlags');
  /** Controls whether the transform payload enables merging tariffs by code. */
  const setTransformCollapseByCode = createFieldSetter(
    'transformCollapseByCode'
  );
  /** Controls whether the transform payload enables base tariff percentages. */
  const setTransformApplyPercentages = createFieldSetter(
    'transformApplyPercentages'
  );
  /** Updates the after-midnight tariff code used in the transform mapping. */
  const setTimeCodeAfterMidnight = createFieldSetter('timeCodeAfterMidnight');
  /** Updates the evening tariff code used in the transform mapping. */
  const setTimeCodeEvening = createFieldSetter('timeCodeEvening');
  /** Updates the weekend daytime tariff code used in the transform mapping. */
  const setTimeCodeWeekend = createFieldSetter('timeCodeWeekend');

  const whenClauses = useMemo(
    () => whenGroups.flatMap((group) => group.clauses),
    [whenGroups]
  );

  const updateWhenClause = (
    rowID: number,
    update: Partial<Omit<WhenClause, 'id'>>,
    groupID?: number
  ): void => {
    setState((current) =>
      updateRuleCriteriaClause(current, rowID, update, groupID)
    );
  };

  const addWhenClause = (groupID?: number): void => {
    setState((current) => addRuleCriteriaClause(current, groupID));
  };

  const removeWhenClause = (rowID: number, groupID?: number): void => {
    setState((current) => removeRuleCriteriaClause(current, rowID, groupID));
  };

  const addWhenGroup = (): void => {
    setState((current) => addRuleCriteriaGroup(current));
  };

  const removeWhenGroup = (groupID: number): void => {
    setState((current) => removeRuleCriteriaGroup(current, groupID));
  };

  const setWhenGroupLogic = (groupID: number, operator: WhenLogic): void => {
    setState((current) =>
      setRuleCriteriaGroupLogic(current, groupID, operator)
    );
  };

  /** Patches an existing validation check by ID, preserving its remaining settings. */
  const updateAssertion = (
    rowID: number,
    update: Partial<Omit<AssertionRow, 'id'>>
  ): void => {
    setAssertions((current) =>
      current.map((assertion) =>
        assertion.id === rowID ? { ...assertion, ...update } : assertion
      )
    );
  };

  /** Appends a default validation check and reserves the next unique row ID. */
  const addAssertion = (): void => {
    setState((current) => ({
      ...current,
      assertions: [
        ...current.assertions,
        createEmptyAssertion(current.nextAssertionID),
      ],
      nextAssertionID: current.nextAssertionID + 1,
    }));
  };

  /** Removes a validation check by ID while retaining the last existing check row. */
  const removeAssertion = (rowID: number): void => {
    setAssertions((current) =>
      current.length === 1
        ? current
        : current.filter((assertion) => assertion.id !== rowID)
    );
  };

  /** Patches the target field or value of an apply-data row without changing its ID. */
  const updateApplyUpdate = (
    rowID: number,
    update: Partial<Omit<ApplyUpdateRow, 'id'>>
  ): void => {
    setState((current) => ({
      ...current,
      applyUpdates: current.applyUpdates.map((applyUpdate) =>
        applyUpdate.id === rowID ? { ...applyUpdate, ...update } : applyUpdate
      ),
    }));
  };

  /** Appends a default apply-data row and advances its ID counter atomically. */
  const addApplyUpdate = (): void => {
    setState((current) => ({
      ...current,
      applyUpdates: [
        ...current.applyUpdates,
        createEmptyApplyUpdate(current.nextApplyUpdateID),
      ],
      nextApplyUpdateID: current.nextApplyUpdateID + 1,
    }));
  };

  /** Removes an apply-data row by ID while retaining one row for outcome editing. */
  const removeApplyUpdate = (rowID: number): void => {
    setState((current) => {
      if (current.applyUpdates.length === 1) return current;
      return {
        ...current,
        applyUpdates: current.applyUpdates.filter(
          (update) => update.id !== rowID
        ),
      };
    });
  };

  /** Maps alert/block choices to validation mode and severity; other choices directly select their rule mode. */
  const onActionChange = (action: RuleAction): void => {
    if (action === RuleSeverity.Block || action === RuleSeverity.Alert) {
      setState((current) => ({
        ...current,
        ruleMode: RuleMode.Validate,
        severity: action,
      }));
      return;
    }
    setState((current) => ({ ...current, ruleMode: action }));
  };

  /** First criteria row used for generated names and legacy tariff metadata. */
  const firstWhenClause = whenClauses[0] || createEmptyWhenClause(1);

  /** Parses the first criteria values for names and metadata, normalizing exact tariff codes. */
  const firstScopeValues = useMemo(
    () =>
      firstWhenClause.scopeMode === ScopeMode.ExactTariff
        ? normalizeExactTariffValues(firstWhenClause.scopeValuesText)
        : parseValueList(firstWhenClause.scopeValuesText),
    [firstWhenClause.scopeMode, firstWhenClause.scopeValuesText]
  );

  /** Collects unique positive exact tariff matches across criteria for billing-history checks. */
  const exactTariffValues = useMemo(
    () =>
      Array.from(
        new Set(
          whenClauses.flatMap((clause) =>
            clause.scopeMode === ScopeMode.ExactTariff &&
            clause.whenOperator === 'is'
              ? normalizeExactTariffValues(clause.scopeValuesText)
              : []
          )
        )
      ),
    [whenClauses]
  );

  const {
    localizedDayOptions,
    localizedWhenOperatorOptions,
    localizedTimeBasedIntervalOptions,
    scopeModeOptions,
    localizedPatientAgeOperatorOptions,
    getDemographicOptions,
    getAssertionDemographicOptions,
    assertionFieldOptions,
  } = getRuleBuilderOptions(translate);

  /** Both the form and summary consume groups without inspecting adjacent rows. */
  const criteriaGroups = useMemo(() => {
    let index = 0;
    return whenGroups.map((group) => {
      const joinerKey =
        group.operator === WhenLogic.All
          ? 'pages:rules.editControls.and'
          : 'pages:rules.editControls.or';
      return {
        key: String(group.id),
        id: group.id,
        logic: group.operator,
        rows: group.clauses.map((clause, rowIndex) => ({
          clause,
          index: index++,
          joinerKey:
            rowIndex === 0 ? 'pages:rules.editControls.when' : joinerKey,
        })),
      };
    });
  }, [whenGroups]);

  const criteriaConditions = useMemo(
    () =>
      whenGroups.map((group) => ({
        operator: group.operator,
        conditions: group.clauses.map((clause) =>
          buildWhenClauseCondition(clause, timezone)
        ),
      })),
    [whenGroups, timezone]
  );

  /** Compiles checks into conditions that match a validation failure, using exact tariffs for history checks. */
  const validationFailureConditions = useMemo(
    () =>
      assertions
        .map((assertion) =>
          buildValidationFailureCondition(assertion, exactTariffValues)
        )
        .filter((condition): condition is RuleCondition => condition !== null),
    [assertions, exactTariffValues]
  );

  /** Generates a fallback rule name from the first criteria field and its values or age comparison. */
  const defaultName = useMemo(() => {
    const modeLabel = translate(
      `pages:rules.createModal.scopeModes.${firstWhenClause.scopeMode}`
    );

    /** Formats age ranges/comparisons or categorical values for the generated name. */
    const getValuesLabel = (): string => {
      if (firstWhenClause.scopeMode !== ScopeMode.PatientAge) {
        return firstScopeValues.join(', ');
      }
      if (
        firstWhenClause.patientAgeOperator ===
        PatientAgeOperator.BetweenInclusive
      ) {
        return `${firstWhenClause.patientAgeStart}-${firstWhenClause.patientAgeEnd}`;
      }
      const operatorLabel = translate(
        `pages:rules.createModal.patientAgeOperators.${firstWhenClause.patientAgeOperator}`
      );
      return `${operatorLabel} ${firstWhenClause.patientAgeValue}`;
    };
    const valuesLabel = getValuesLabel();
    return valuesLabel
      ? `${modeLabel}: ${valuesLabel}`
      : translate('pages:rules.createModal.defaultRuleName');
  }, [
    firstScopeValues,
    firstWhenClause.scopeMode,
    firstWhenClause.patientAgeEnd,
    firstWhenClause.patientAgeOperator,
    firstWhenClause.patientAgeStart,
    firstWhenClause.patientAgeValue,
    translate,
  ]);

  /** Trimmed explicit name or generated fallback used for saving, previews, and event naming. */
  const effectiveRuleName = ruleName.trim() || defaultName;

  /** Validation event identifier selected from the current alert/block severity. */
  const eventCode =
    severity === RuleSeverity.Block ? 'RULE_BLOCK' : 'RULE_ALERT';

  /** Stable event naming format built from the effective rule name for the generated body. */
  const eventType = `rule-${toSlug(effectiveRuleName) || 'draft'}-matched`;

  /** Trimmed validation message or generated fallback passed to the rule event and previews. */
  const effectiveRuleMessage =
    ruleMessage.trim() || `${effectiveRuleName} failed validation.`;

  /** Trimmed flag title or rule-name fallback used for outcome validation and event data. */
  const effectiveFlagLabel = flagLabel.trim() || effectiveRuleName;

  /** Trimmed flag description or generated fallback used by flag events and previews. */
  const effectiveFlagMessage =
    flagMessage.trim() || `${effectiveFlagLabel} needs review.`;

  /** Parses the apply-data priority input before checking whether a default is needed. */
  const parsedPriority = Number.parseInt(applyPriority, 10);

  /** Finite apply-data priority, defaulting to one when the input cannot be parsed. */
  const effectiveApplyPriority = Number.isFinite(parsedPriority)
    ? parsedPriority
    : 1;

  /** Parses the tariff-transform priority input before applying its fallback. */
  const parsedTransformPriority = Number.parseInt(transformPriority, 10);

  /** Finite tariff-transform priority, defaulting to one for unparseable input. */
  const effectiveTransformPriority = Number.isFinite(parsedTransformPriority)
    ? parsedTransformPriority
    : 1;

  /** Executable body and builder metadata regenerated from the current form inputs. */
  const formBody = useMemo(
    () =>
      buildRuleBody({
        assertions,
        applyIntervalMinutes,
        applyUpdates,
        applyBehavior,
        applyNotification,
        effectiveApplyPriority,
        effectiveTransformPriority,
        effectiveFlagLabel,
        effectiveFlagMessage,
        effectiveRuleName,
        effectiveRuleMessage,
        eventCode,
        eventType,
        firstScopeValues,
        firstScopeMode: firstWhenClause.scopeMode,
        flagSeverity,
        ruleMode,
        severity,
        timeCodeAfterMidnight,
        timeCodeEvening,
        timeCodeWeekend,
        transformAppendFlags,
        transformApplyPercentages,
        transformCollapseByCode,
        validationFailureConditions,
        whenGroups,
        criteriaConditions,
        whenLogic,
      }),
    [
      assertions,
      applyIntervalMinutes,
      applyUpdates,
      applyBehavior,
      applyNotification,
      effectiveApplyPriority,
      effectiveTransformPriority,
      effectiveFlagLabel,
      effectiveFlagMessage,
      effectiveRuleName,
      effectiveRuleMessage,
      eventCode,
      eventType,
      firstScopeValues,
      firstWhenClause.scopeMode,
      flagSeverity,
      ruleMode,
      severity,
      timeCodeAfterMidnight,
      timeCodeEvening,
      timeCodeWeekend,
      transformAppendFlags,
      transformApplyPercentages,
      transformCollapseByCode,
      validationFailureConditions,
      whenGroups,
      criteriaConditions,
      whenLogic,
    ]
  );

  /** Current action, criteria, outcome, and name values exposed for section-level change tracking. */
  const editSnapshot: RuleEditorSnapshot = {
    action: {
      ruleMode,
      severity: ruleMode === RuleMode.Validate ? severity : undefined,
    },
    criteria: {
      whenLogic,
      whenGroups,
    },
    outcome: {
      ...outcomeSnapshot,
      ...(ruleMode === RuleMode.Validate ? { assertions } : {}),
    },
    name: ruleName,
  };

  /** Mount-time comparison baseline used to preserve saved sections the user has not edited. */
  const initialEditSnapshot = useRef({
    sections: editSnapshot,
    outcome: outcomeSnapshot,
    assertions,
  });

  /** Preserves unsupported bodies and replaces only edited sections of supported rules. */
  const getEditedBody = (): Record<string, unknown> => {
    if (!isFormSupported && initialBody) return initialBody;
    if (!initialBody) return formBody;
    return preserveRuleBody(initialBody, formBody, {
      action: !isEqual(
        editSnapshot.action,
        initialEditSnapshot.current.sections.action
      ),
      criteria: !isEqual(
        editSnapshot.criteria,
        initialEditSnapshot.current.sections.criteria
      ),
      checks: !isEqual(assertions, initialEditSnapshot.current.assertions),
      outcome: !isEqual(outcomeSnapshot, initialEditSnapshot.current.outcome),
    });
  };

  /** Save payload preserving existing executable logic when it has not been edited. */
  const body = getEditedBody();

  /** Requires at least one group and a successfully compiled condition in every row. */
  const criteriaValid = hasCompleteRuleCriteria(criteriaConditions);
  /** Validates the selected outcome using current inputs, derived flag text, and compiled checks. */
  const outcomeValid = isRuleOutcomeValid(
    { ...state, assertions },
    {
      effectiveFlagLabel,
      exactTariffValues,
      validationFailureConditions,
    }
  );

  /** Unsupported rules allow metadata edits while retaining their original body. */
  const canSubmit = isFormSupported
    ? criteriaValid && outcomeValid
    : Boolean(initialBody);

  /** Action picker value combining validation mode with its alert/block severity. */
  const selectedAction: RuleAction =
    ruleMode === RuleMode.Validate ? severity : ruleMode;

  // Expose inputs, row actions, derived options, and save results to the editor components.
  return {
    timezone,
    criteriaValid,
    outcomeValid,
    translate,
    ruleName,
    setRuleName,
    whenLogic,
    setWhenLogic,
    whenClauses,
    whenGroups,
    addWhenGroup,
    removeWhenGroup,
    setWhenGroupLogic,
    criteriaGroups,
    scopeModeOptions,
    updateWhenClause,
    localizedWhenOperatorOptions,
    getDemographicOptions,
    removeWhenClause,
    localizedPatientAgeOperatorOptions,
    localizedDayOptions,
    addWhenClause,
    addAssertion,
    assertions,
    removeAssertion,
    assertionFieldOptions,
    updateAssertion,
    getAssertionDemographicOptions,
    ruleMode,
    applyUpdates,
    applyBehavior,
    applyNotification,
    updateApplyUpdate,
    setApplyBehavior,
    setApplyNotification,
    localizedTimeBasedIntervalOptions,
    applyIntervalMinutes,
    setApplyIntervalMinutes,
    removeApplyUpdate,
    addApplyUpdate,
    applyPriority,
    setApplyPriority,
    transformAppendFlags,
    setTransformAppendFlags,
    transformCollapseByCode,
    setTransformCollapseByCode,
    transformApplyPercentages,
    setTransformApplyPercentages,
    timeCodeAfterMidnight,
    setTimeCodeAfterMidnight,
    timeCodeEvening,
    setTimeCodeEvening,
    timeCodeWeekend,
    setTimeCodeWeekend,
    transformPriority,
    setTransformPriority,
    flagLabel,
    setFlagLabel,
    flagMessage,
    setFlagMessage,
    flagSeverity,
    setFlagSeverity,
    severity,
    setSeverity,
    ruleMessage,
    setRuleMessage,
    exactTariffValues,
    body,
    formBody,
    canSubmit,
    editSnapshot,
    effectiveRuleName,
    effectiveRuleMessage,
    effectiveFlagLabel,
    effectiveFlagMessage,
    selectedAction,
    onActionChange,
    isFormSupported,
    whenConditions: criteriaConditions.flatMap((group) =>
      group.conditions.filter(
        (condition): condition is RuleCondition => condition !== null
      )
    ),
  };
};
